import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { useListParams } from '@/hooks/useListParams'
import {
  useServiceCategories,
  useCreateServiceCategory,
  useUpdateServiceCategory,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { ServiceCategory } from '@/types'

const NAME_MAX = 100
const DESC_MAX = 500
const COLUMN_COUNT = 5

const cardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const fieldInputClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const fieldTextareaClass = cn(
  'min-h-[80px] rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const drawerClass =
  'tf-right-drawer-short *:min-w-0 [&>button]:right-[18px] [&>button]:top-[18px] [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const dialogBtnOutlineClass =
  'rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium text-[#171a20] shadow-none hover:bg-[#f8f9fb]'

const dialogBtnPrimaryClass =
  'rounded-xl bg-[#ff5a1f] px-3 py-3 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'

const switchClass =
  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.02em] text-[#3d434e]'

const tdClass =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm text-[#171a20]'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionBtnClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
function StatusTag({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        active
          ? { backgroundColor: '#D1FAE5', color: '#065F46' }
          : { backgroundColor: '#e6e6e6', color: '#1e1e1e' }
      }
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

// 2.11.5 — Service category management. The 8 canonical categories are seeded;
// the user can also create new categories (feedback #84 overrides the stale
// "categories cannot be added" Figma caption). Form: name (required, max 100),
// description (optional, max 500), active toggle.
export function CategoriesPanel() {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useServiceCategories(query)
  const create = useCreateServiceCategory()
  const update = useUpdateServiceCategory()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<ServiceCategory | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const rows = useMemo(() => data?.data ?? [], [data])

  function openEdit(cat: ServiceCategory) {
    setEditing(cat)
    setName(cat.name)
    setDescription(cat.description ?? '')
    setIsActive(cat.is_active)
    setErrors({})
    setOpen(true)
  }

  function validate() {
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = t('categories.validation.nameRequired')
    else if (name.trim().length > NAME_MAX)
      next.name = t('categories.validation.nameMax', { max: NAME_MAX })
    if (description.length > DESC_MAX)
      next.description = t('categories.validation.descriptionMax', {
        max: DESC_MAX,
      })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  function save() {
    if (!validate()) return
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      is_active: isActive,
    }
    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing
          ? t('categories.toast.updated')
          : t('categories.toast.created'),
      })
      setOpen(false)
    }
    const onError = (e: unknown) =>
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess, onError })
    else create.mutate(body, { onSuccess, onError })
  }

  function toggleActive(cat: ServiceCategory) {
    update.mutate(
      { id: cat.id, body: { name: cat.name, is_active: !cat.is_active } },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: cat.is_active
              ? t('categories.toast.deactivated')
              : t('categories.toast.activated'),
          }),
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      }
    )
  }

  return (
    <>
      <section className={cardClass}>
        <header className='flex items-center gap-2 overflow-hidden pb-2 pt-6 pr-6'>
          <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
            <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('categories.title')}
            </h2>
          </div>
        </header>

        <div className='flex flex-col gap-4 px-6 pb-4 pt-4'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[720px] caption-bottom text-sm'>
              <TableHeader>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, 'w-[320px]')}>
                    {t('common.name')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[120px] text-center')}>
                    {t('common.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[160px] text-center')}>
                    {t('categories.columnItemsCount')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[180px]')}>
                    {t('categories.columnLastUpdated')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[112px] text-right')}>
                    <span>{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('categories.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('categories.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  rows.map(cat => {
                    return (
                      <TableRow key={cat.id} className='group border-0'>
                        <TableCell className={cn(tdClass)}>
                          <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                            {cat.name}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <StatusTag active={cat.is_active} />
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <span className='text-sm leading-[1.4] text-[#171a20]'>
                            {cat.service_items_count ?? 0}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                            {formatDate(cat.updated_at)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <div className='flex items-center justify-end gap-2'>
                            <TruncatedTextTooltip
                              content={
                                cat.is_active
                                  ? t('categories.actions.deactivate')
                                  : t('categories.actions.activate')
                              }
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  'bg-[#f4f5f8]',
                                  accentActionBtnClass
                                )}
                                aria-label={
                                  cat.is_active
                                    ? t('categories.actions.deactivate')
                                    : t('categories.actions.activate')
                                }
                                disabled={update.isPending}
                                onClick={() => toggleActive(cat)}
                              >
                                {cat.is_active ? (
                                  <EyeOff className='size-4' />
                                ) : (
                                  <Eye className='size-4' />
                                )}
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.edit')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  'bg-[#f4f5f8]',
                                  accentActionBtnClass
                                )}
                                aria-label={t('common:actions.edit')}
                                onClick={() => openEdit(cat)}
                              >
                                <Pencil className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            {/* Deleting categories is disabled: only the 8 seeded
                                defaults exist and must stay (Edit-only). */}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>

          <ListPagination
            metadata={data?.metadata}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showSummary={false}
          />
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          side='right'
          showCloseButton={false}
          className={drawerClass}
        >
          <SettingsDrawerHeader
            title={
              editing
                ? t('categories.dialog.editTitle')
                : t('categories.dialog.addTitle')
            }
            closeDisabled={create.isPending || update.isPending}
            titleClassName='text-base'
          />

          <div className='flex flex-1 flex-col overflow-y-auto bg-[#f4f5f8]'>
            <div className='flex flex-1 flex-col gap-4 px-6 py-6'>
              <div className={cardClass}>
                <div className='border-b border-transparent px-0 pb-2 pt-4'>
                  <div className='flex items-center border-l-4 border-[#ff5a1f] px-4'>
                    <h3 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
                      INFORMATION
                    </h3>
                  </div>
                </div>

                <div
                  key={editing?.id ?? 'create'}
                  className='flex flex-col gap-6 px-4 pb-6 pt-4'
                >
                  <div className='flex flex-col gap-1'>
                    <Label
                      htmlFor='cat-name'
                      className='text-xs font-medium leading-4 text-[#171a20]'
                    >
                      {t('categories.fields.name')}
                      <span className='text-[#ec221f]'> *</span>
                    </Label>
                    <Input
                      id='cat-name'
                      value={name}
                      maxLength={NAME_MAX}
                      placeholder={t('categories.fields.enterHere')}
                      onChange={e => {
                        setName(e.target.value)
                        setErrors(s => ({ ...s, name: '' }))
                      }}
                      className={cn(
                        fieldInputClass,
                        errors.name && 'border border-destructive'
                      )}
                    />
                    {errors.name && (
                      <p className='text-sm text-destructive'>{errors.name}</p>
                    )}
                  </div>

                  <div className='flex flex-col gap-1'>
                    <Label
                      htmlFor='cat-desc'
                      className='text-xs font-medium leading-4 text-[#171a20]'
                    >
                      {t('categories.fields.description')}
                    </Label>
                    <Textarea
                      id='cat-desc'
                      value={description}
                      maxLength={DESC_MAX}
                      placeholder={t('categories.fields.enterHere')}
                      onChange={e => {
                        setDescription(e.target.value)
                        setErrors(s => ({ ...s, description: '' }))
                      }}
                      className={cn(
                        fieldTextareaClass,
                        errors.description && 'border border-destructive'
                      )}
                    />
                    {errors.description && (
                      <p className='text-sm text-destructive'>
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className='border-b border-[#e7e9ef] pb-3'>
                    <div className='flex items-center gap-3'>
                      <Label
                        htmlFor='cat-active'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('categories.fields.active')}
                      </Label>
                      <Switch
                        id='cat-active'
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        className={switchClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='tf-right-drawer-footer sticky bottom-0 justify-end'>
              <Button
                type='button'
                variant='outline'
                className={dialogBtnOutlineClass}
                onClick={() => setOpen(false)}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                type='button'
                className={dialogBtnPrimaryClass}
                onClick={save}
                disabled={create.isPending || update.isPending}
              >
                {t('common:actions.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CategoriesPanel
